import { CATEGORICAL_FALLBACK } from '../render/colorScale';
import type { Size } from '../types';
import { toAngularTicks } from './radialAxis';
import type { RadialPlot } from './types';

export type MeasureText = (text: string) => number;

export type ScreenPoint = { x: number; y: number };

/** A series drawn in a categorical slot, or folded into the ink-toned tail. */
export type SeriesStyle = { kind: 'slot'; color: string } | { kind: 'tail' };

export type LegendItem = {
  label: string;
  style: SeriesStyle;
  x: number;
  y: number;
};

type AngularLabel = ScreenPoint & {
  text: string;
  align: CanvasTextAlign;
  baseline: CanvasTextBaseline;
};

export type RadialLayout = {
  center: ScreenPoint;
  radius: number;
  /** Only the labels that fit without overprinting their neighbours. */
  angularLabels: AngularLabel[];
  legend: LegendItem[];
  captions: { text: string; x: number; y: number }[];
};

export type ProjectedRadialPoint = ScreenPoint & {
  seriesIndex: number;
  pointIndex: number;
};

const LINE_HEIGHT = 16;
const LABEL_GAP = 8;
const MAX_SPOKE_LABEL_WIDTH = 120;
const PADDING = 12;
const SWATCH = 10;
const SWATCH_GAP = 6;
const LEGEND_ITEM_GAP = 16;
const HOVER_RADIUS = 14;

export const toSeriesStyle = (seriesIndex: number): SeriesStyle => {
  const color = CATEGORICAL_FALLBACK[seriesIndex];
  return color ? { kind: 'slot', color } : { kind: 'tail' };
};

type LegendEntry = { label: string; style: SeriesStyle };

export const toLegendEntries = (
  plot: RadialPlot,
  formatOtherSeries: (count: number) => string
): LegendEntry[] => {
  const slotted = plot.series
    .slice(0, CATEGORICAL_FALLBACK.length)
    .map(({ label }, index) => ({ label, style: toSeriesStyle(index) }));
  const tailCount = plot.series.length - slotted.length;

  if (tailCount === 0) return slotted;
  return [
    ...slotted,
    { label: formatOtherSeries(tailCount), style: { kind: 'tail' } },
  ];
};

type FlowedLegend = { items: LegendItem[]; height: number };

const flowLegend = (
  entries: LegendEntry[],
  width: number,
  measure: MeasureText
): FlowedLegend => {
  if (entries.length === 0) return { items: [], height: 0 };

  const available = width - PADDING * 2;
  const { items, row } = entries.reduce<{
    items: LegendItem[];
    row: number;
    x: number;
  }>(
    ({ items, row, x }, entry) => {
      const itemWidth = SWATCH + SWATCH_GAP + measure(entry.label);
      const wraps = x > 0 && x + itemWidth > available;
      const nextRow = wraps ? row + 1 : row;
      const nextX = wraps ? 0 : x;
      return {
        items: [
          ...items,
          { ...entry, x: PADDING + nextX, y: nextRow * LINE_HEIGHT },
        ],
        row: nextRow,
        x: nextX + itemWidth + LEGEND_ITEM_GAP,
      };
    },
    { items: [], row: 0, x: 0 }
  );

  return { items, height: (row + 1) * LINE_HEIGHT + PADDING };
};

const toLabelGutter = (
  plot: RadialPlot,
  measure: MeasureText,
  format: (value: number) => string
): number => {
  const { angularAxis } = plot;
  const labels =
    angularAxis.kind === 'categorical'
      ? angularAxis.spokes.map(({ label }) => label)
      : toAngularTicks(angularAxis.axis).map(({ value }) => format(value));
  const widest = labels.reduce(
    (widest, label) => Math.max(widest, measure(label)),
    0
  );
  return Math.min(widest, MAX_SPOKE_LABEL_WIDTH) + LABEL_GAP;
};

export const polarToScreen = (
  center: ScreenPoint,
  distance: number,
  angle: number
): ScreenPoint => ({
  x: center.x + distance * Math.sin(angle),
  y: center.y - distance * Math.cos(angle),
});

export const truncateToWidth = (
  text: string,
  maxWidth: number,
  measure: MeasureText
): string => {
  if (measure(text) <= maxWidth) return text;
  const characters = Array.from(text);
  const fittingCount = characters.findIndex(
    (_, index) =>
      measure(`${characters.slice(0, index + 1).join('')}…`) > maxWidth
  );
  return `${characters.slice(0, fittingCount).join('')}…`;
};

const alignFor = (
  angle: number
): { align: CanvasTextAlign; baseline: CanvasTextBaseline } => {
  const sin = Math.sin(angle);
  const cos = Math.cos(angle);
  return {
    align: sin > 0.2 ? 'left' : sin < -0.2 ? 'right' : 'center',
    baseline: cos > 0.2 ? 'bottom' : cos < -0.2 ? 'top' : 'middle',
  };
};

type LabelBox = { left: number; top: number; right: number; bottom: number };

const toLabelBox = (label: AngularLabel, width: number): LabelBox => {
  const left =
    label.align === 'left'
      ? label.x
      : label.align === 'right'
        ? label.x - width
        : label.x - width / 2;
  const top =
    label.baseline === 'top'
      ? label.y
      : label.baseline === 'bottom'
        ? label.y - LINE_HEIGHT
        : label.y - LINE_HEIGHT / 2;
  return { left, top, right: left + width, bottom: top + LINE_HEIGHT };
};

const overlaps = (a: LabelBox, b: LabelBox): boolean =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

type BoxedLabel = { label: AngularLabel; box: LabelBox };

/** Keeps each label that clears the last one kept, and, going round, the first. */
const dropOverlapping = (labels: BoxedLabel[]): AngularLabel[] =>
  labels
    .reduce<BoxedLabel[]>((kept, candidate) => {
      const previous = kept.at(-1);
      const first = kept[0];
      const clears =
        (!previous || !overlaps(previous.box, candidate.box)) &&
        (!first || first === previous || !overlaps(first.box, candidate.box));
      return clears ? [...kept, candidate] : kept;
    }, [])
    .map(({ label }) => label);

type AngularLabelArgs = {
  plot: RadialPlot;
  center: ScreenPoint;
  radius: number;
  format: (value: number) => string;
  measure: MeasureText;
};

const toAngularLabels = ({
  plot,
  center,
  radius,
  format,
  measure,
}: AngularLabelArgs): AngularLabel[] => {
  const { angularAxis } = plot;
  const texts =
    angularAxis.kind === 'numeric'
      ? toAngularTicks(angularAxis.axis).map(({ angle, value }) => ({
          angle,
          text: format(value),
        }))
      : angularAxis.spokes.map(({ angle, label }) => ({
          angle,
          text: truncateToWidth(label, MAX_SPOKE_LABEL_WIDTH, measure),
        }));

  return dropOverlapping(
    texts.map(({ angle, text }) => {
      const label = {
        ...polarToScreen(center, radius + LABEL_GAP, angle),
        ...alignFor(angle),
        text,
      };
      return { label, box: toLabelBox(label, measure(text)) };
    })
  );
};

export type RadialLayoutArgs = {
  viewport: Size;
  plot: RadialPlot;
  legendEntries: LegendEntry[];
  axisCaptions: readonly string[];
  format: (value: number) => string;
  measure: MeasureText;
};

export const toRadialLayout = ({
  viewport,
  plot,
  legendEntries,
  axisCaptions,
  format,
  measure,
}: RadialLayoutArgs): RadialLayout => {
  const captionHeight = axisCaptions.length * LINE_HEIGHT;
  const legend = flowLegend(legendEntries, viewport.width, measure);
  const top = PADDING + captionHeight;
  const bottom = viewport.height - legend.height - PADDING;
  const horizontalGutter = toLabelGutter(plot, measure, format);
  const verticalGutter = LINE_HEIGHT + LABEL_GAP;

  const radius = Math.max(
    0,
    Math.min(
      viewport.width / 2 - PADDING - horizontalGutter,
      (bottom - top) / 2 - verticalGutter
    )
  );
  const center = { x: viewport.width / 2, y: (top + bottom) / 2 };

  return {
    center,
    radius,
    angularLabels: toAngularLabels({ plot, center, radius, format, measure }),
    legend: legend.items.map(item => ({
      ...item,
      y: bottom + PADDING + item.y,
    })),
    captions: axisCaptions.map((text, index) => ({
      text,
      x: PADDING,
      y: PADDING + index * LINE_HEIGHT,
    })),
  };
};

export const toDistance = (
  plot: RadialPlot,
  radius: number,
  value: number
): number => {
  const [min, max] = plot.radialAxis.range;
  return ((value - min) / (max - min)) * radius;
};

export const projectRadialPoints = (
  plot: RadialPlot,
  { center, radius }: RadialLayout
): ProjectedRadialPoint[] =>
  plot.series.flatMap((series, seriesIndex) =>
    series.points.flatMap((point, pointIndex) =>
      point.value === null
        ? []
        : [
            {
              ...polarToScreen(
                center,
                toDistance(plot, radius, point.value),
                point.angle
              ),
              seriesIndex,
              pointIndex,
            },
          ]
    )
  );

/**
 * Nearest within a radius. Coinciding vertices go to the series painted on top: the hovered series
 * if it is one of them, otherwise the lowest index.
 */
export const findRadialPointAt = (
  projected: ProjectedRadialPoint[],
  cursor: ScreenPoint,
  hoveredSeriesIndex: number | null
): ProjectedRadialPoint | null =>
  projected.reduce<{ best: ProjectedRadialPoint | null; distance: number }>(
    (nearest, candidate) => {
      const distance =
        (candidate.x - cursor.x) ** 2 + (candidate.y - cursor.y) ** 2;
      const isCloser = distance < nearest.distance;
      const winsTie =
        distance === nearest.distance &&
        nearest.best !== null &&
        candidate.seriesIndex === hoveredSeriesIndex;
      return isCloser || winsTie ? { best: candidate, distance } : nearest;
    },
    { best: null, distance: HOVER_RADIUS ** 2 }
  ).best;
